import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Link2 } from "lucide-react";

export default function Unmatched() {
  const { user } = useAuth();
  const canMatch = user.role === "owner" || user.role === "partman";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [match, setMatch] = useState(null);
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/receiving/unmatched").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const searchJobs = async () => {
    if (!q.trim()) return;
    const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
    setCandidates(data.jobs || []);
  };

  const doMatch = async (jobId) => {
    setSaving(true);
    try {
      await api.post(`/receiving/${match.id}/match`, { job_id: jobId });
      toast.success("Berhasil dicocokkan ke SPK.");
      setMatch(null); setCandidates([]); setQ(""); load();
    } catch (err) {
      toast.error(apiError(err, "Gagal match"));
    } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Unmatched Receiving" subtitle="Part datang yang belum cocok dengan SPK manapun. Review & manual match." />
      {loading ? <Spinner /> : rows.length === 0 ? <EmptyState title="Tidak ada unmatched" subtitle="Semua receiving sudah tercocokkan." icon={AlertTriangle} /> : (
        <div className="grid gap-2">
          {rows.map((r) => (
            <Card key={r.id} className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono-data font-semibold text-gray-900">{r.nopol || "Nopol tidak ada"}</div>
                <div className="text-sm text-gray-600 truncate">{(r.items || []).map((i) => `${i.part_number} ×${i.qty}`).join(", ")}</div>
                <div className="text-xs text-gray-400 mt-0.5">{r.receiving_number || "no-rcv"} • {r.created_by}</div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={r.need_matching ? "NEED_MATCHING" : "UNMATCHED"} />
                {canMatch && <Button size="sm" variant="outline" onClick={() => setMatch(r)} data-testid={`match-btn-${r.id}`}><Link2 size={14} className="mr-1" />Match</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!match} onOpenChange={(o) => !o && (setMatch(null), setCandidates([]))}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Manual Match ke SPK</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="Cari Nopol / Claim / SPK" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchJobs()} data-testid="match-search-input" />
            <Button onClick={searchJobs} className="bg-gray-900">Cari</Button>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {candidates.map((j) => (
              <button key={j.id} onClick={() => doMatch(j.id)} disabled={saving} className="w-full text-left border border-gray-200 rounded-lg p-3 hover:bg-gray-50" data-testid={`match-candidate-${j.id}`}>
                <div className="font-mono-data font-semibold">{j.nopol}</div>
                <div className="text-xs text-gray-400">{j.claim_number || "—"} • {j.spk_number || "—"} • {j.job_number}</div>
              </button>
            ))}
            {candidates.length === 0 && <div className="text-sm text-gray-400 text-center py-4">Cari job untuk dicocokkan.</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => (setMatch(null), setCandidates([]))}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
